import { useState } from 'react'
import {
  Copy,
  Check,
  X,
  RotateCcw,
  FileText,
  FileVideo,
  ImageIcon,
  Clock,
  AlertCircle,
  Mic,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { AIChatAttachment, AIChatMessage } from '@/stores/aiChatTypes'
import { ChatMessageMetadataCards } from '@/components/layout/ChatMessageMetadataCards'
import { MarkdownContent } from '@/components/chat/MarkdownContent'
import {
  MessageActionButton,
  MessageActions,
  ReadAloudButton,
} from '@/components/chat/MessageActions'

// Individual message component
export function ChatMessage({
  message,
  onCopy,
  onEditImage,
  onDraftEdit,
  onDraftSchedule,
  onDraftDelete,
  onScheduleResolve,
  onScheduleDismiss,
  onRetry,
  onRetryMessage,
  readOnly = false,
  isSpeaking = false,
  isSpeechLoading = false,
  onPlaySpeech,
  onStopSpeech,
}: {
  message: AIChatMessage
  onCopy: (text: string) => void
  onEditImage?: (imageUrl: string) => void
  onDraftEdit?: (draftId: string) => void
  /** Opens the scheduling widget under THIS message (metadata.scheduling_card). */
  onDraftSchedule?: (draftId: string, messageId: string) => void | Promise<void>
  onDraftDelete?: (draftId: string) => void | Promise<void>
  /** Schedule (ISO time) or publish-now (null) the metadata.scheduling_card draft. */
  onScheduleResolve?: (messageId: string, scheduledAt: string | null) => void | Promise<void>
  onScheduleDismiss?: (messageId: string) => void
  onRetry?: () => void
  /** Resend THIS user bubble — shown only when `message.status === 'failed'`. */
  onRetryMessage?: (messageId: string) => void
  /**
   * When true, hide interactive action affordances (copy button, retry,
   * edit-image overlay) so the message renders as a passive transcript
   * row. Used by admin replay/conversation viewers.
   */
  readOnly?: boolean
  /** Read aloud — this message is being read out now. */
  isSpeaking?: boolean
  /** This message's spoken audio is being requested/buffered. */
  isSpeechLoading?: boolean
  onPlaySpeech?: (messageId: string, text: string) => void
  onStopSpeech?: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [expandedAttachment, setExpandedAttachment] = useState<AIChatAttachment | null>(null)
  const isUser = message.role === 'user'
  // A user message that hasn't reached the server must never look
  // identical to a delivered one — that gap is exactly how an offline send
  // got silently lost with no error and no indicator.
  const isPendingSend = isUser && message.status === 'pending'
  const isFailedSend = isUser && message.status === 'failed'
  // A finished agent reply carries its actions (Copy, Read aloud) in a row under it, as ChatGPT
  // does; the owner's own bubble and an error keep Copy on hover.
  const hasAgentActions =
    !readOnly && !isUser && !message.isError && !message.isStreaming && !!message.content

  const handleCopyMessage = () => {
    onCopy(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div
      className={cn('group flex flex-col', isUser ? 'items-end' : 'items-start')}
      data-testid={`chat-message-${message.role}`}
    >
      {/* A team member's own post names it; the owner's answer names whom it went to. */}
      {message.metadata?.team_member && (
        <span
          className="text-muted-foreground mb-1 px-1 text-xs font-medium"
          data-testid="team-member-label"
        >
          {isUser ? `To ${message.metadata.team_member.name}` : message.metadata.team_member.name}
        </span>
      )}
      {/* Message content. The user's turn is the ONE bubble in the transcript;
          assistant turns render unboxed on the surface so their inline cards
          (activity, drafts) are never a card inside a card
. Errors keep a bordered destructive container — that tint is
          status, not decoration. */}
      <div
        data-slot={isUser || message.isError ? 'chat-bubble' : undefined}
        className={cn(
          'relative',
          message.isError
            ? 'border-destructive/20 bg-destructive/5 text-destructive max-w-[85%] rounded-2xl border px-4 py-3 wrap-anywhere'
            : isUser
              ? cn(
                  'bg-owner-bubble text-owner-bubble-foreground max-w-[78%] rounded-[20px] px-4 py-3 wrap-anywhere',
                  isPendingSend && 'opacity-60',
                  isFailedSend && 'border-destructive border opacity-90'
                )
              : 'w-full'
        )}
      >
        {/* Copy button (the owner's bubble, an error) */}
        {!readOnly && (isUser || message.isError) && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleCopyMessage}
                aria-label={copied ? 'Message copied' : 'Copy message'}
                className={cn(
                  'absolute -top-2 right-0 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100 pointer-coarse:before:absolute pointer-coarse:before:-inset-2.5',
                  isUser ? 'text-owner-bubble-foreground/70 hover:text-owner-bubble-foreground' : ''
                )}
                data-testid="copy-message-button"
              >
                {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">{copied ? 'Copied!' : 'Copy message'}</TooltipContent>
          </Tooltip>
        )}

        {/* Message body — a dedicated testid so tests can wait for actual
            response content without matching the timestamp footer below,
            which always renders (and, being a time string like "3:41 PM",
            always satisfies a bare /\w/ content check even while this block
            is still showing the typing indicator). */}
        {/* A card-only message (a platform request) has no body bubble. */}
        {(isUser || message.content || message.isStreaming || message.isError) && (
          <div
            data-testid="chat-message-body"
            data-slot={!isUser && !message.isError ? 'chat-bubble' : undefined}
            className={cn(
              !isUser &&
                !message.isError &&
                'bg-agent-bubble text-agent-bubble-foreground w-fit max-w-[78%] rounded-[20px] px-4 py-3 wrap-anywhere'
            )}
          >
            {message.isStreaming && !message.content ? (
              <div className="py-1">
                <div className="flex items-center gap-1" data-testid="typing-indicator">
                  <span className="bg-muted-foreground/60 size-1.5 animate-bounce rounded-full [animation-delay:-0.3s]" />
                  <span className="bg-muted-foreground/60 size-1.5 animate-bounce rounded-full [animation-delay:-0.15s]" />
                  <span className="bg-muted-foreground/60 size-1.5 animate-bounce rounded-full" />
                </div>
                {message.pendingStatus && (
                  <p
                    className="text-muted-foreground mt-2 text-sm"
                    data-testid="pending-status"
                    role="status"
                  >
                    {message.pendingStatus}
                  </p>
                )}
              </div>
            ) : isUser ? (
              <p className="text-[0.9375rem] leading-[1.45] whitespace-pre-wrap">
                {message.content}
              </p>
            ) : (
              <MarkdownContent content={message.content} isStreaming={message.isStreaming} />
            )}
          </div>
        )}

        {/* Attachment thumbnails */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="message-attachments">
            {message.attachments.map((att, idx) =>
              att.file_id ? (
                // A document kept in Files: the agent reads it there by its id.
                <span
                  key={att.file_id}
                  className="bg-muted text-muted-foreground flex h-8 max-w-56 items-center gap-1.5 rounded-md border px-2.5"
                  data-testid="message-document-attachment"
                  title={att.filename}
                >
                  <FileText className="size-3.5 shrink-0" />
                  <span className="text-foreground truncate text-xs">{att.filename}</span>
                </span>
              ) : att.content_type?.startsWith('audio/') ? (
                // A voice note (its transcript is the message text above) or a recording the
                // owner attached, by its name.
                <span
                  key={att.upload_id || idx}
                  className="bg-muted text-muted-foreground flex h-8 max-w-56 items-center gap-1.5 rounded-md border px-2.5"
                  data-testid="message-audio-attachment"
                  title={att.filename}
                >
                  <Mic className="size-3.5 shrink-0" />
                  <span className="text-foreground truncate text-xs">{att.filename}</span>
                </span>
              ) : (
                <button
                  key={att.upload_id || idx}
                  type="button"
                  onClick={() => setExpandedAttachment(att)}
                  className="cursor-pointer overflow-hidden rounded-md border transition-opacity hover:opacity-80"
                >
                  {att.content_type?.startsWith('video/') ? (
                    // Named tile, not a thumbnail — the composer doesn't decode a
                    // poster frame, so there is nothing to show here either.
                    <span
                      className="bg-muted text-muted-foreground flex h-16 max-w-40 items-center gap-2 px-2.5"
                      data-testid="message-video-attachment"
                    >
                      <FileVideo className="size-5 shrink-0" />
                      <span className="text-foreground truncate text-xs">{att.filename}</span>
                    </span>
                  ) : att.content_type?.startsWith('image/hei') ? (
                    // HEIC and HEIF: a photo most browsers cannot draw, named instead.
                    <span
                      className="bg-muted text-muted-foreground flex h-16 max-w-40 items-center gap-2 px-2.5"
                      data-testid="message-heic-attachment"
                    >
                      <ImageIcon className="size-5 shrink-0" />
                      <span className="text-foreground truncate text-xs">{att.filename}</span>
                    </span>
                  ) : (
                    <img src={att.url} alt={att.filename} className="size-16 object-cover" />
                  )}
                </button>
              )
            )}
          </div>
        )}

        {/* Timestamp — quiet meta, one type step below body — and an agent reply's actions */}
        <div
          className={cn(
            'mt-1 flex items-center gap-1.5 text-xs pointer-coarse:gap-4',
            isUser ? 'text-owner-bubble-foreground/60' : 'text-muted-foreground/70'
          )}
        >
          <span>
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {hasAgentActions && (
            <MessageActions>
              <MessageActionButton
                label={copied ? 'Message copied' : 'Copy message'}
                tooltip={copied ? 'Copied!' : 'Copy'}
                testId="copy-message-button"
                onClick={handleCopyMessage}
              >
                {copied ? <Check /> : <Copy />}
              </MessageActionButton>
              <ReadAloudButton
                isSpeaking={isSpeaking}
                isLoading={isSpeechLoading}
                onPlay={() => onPlaySpeech?.(message.id, message.content)}
                onStop={() => onStopSpeech?.()}
              />
            </MessageActions>
          )}
        </div>

        {/* Delivery state for a user message that hasn't reached the server
            yet — pending fades in place, failed gets its own retry so
            the owner never has to trust a bubble that looks identical to a
            delivered one. */}
        {isPendingSend && (
          <div
            className="text-owner-bubble-foreground/70 mt-1 flex items-center gap-1 text-[11px]"
            data-testid="message-pending-indicator"
          >
            <Clock className="size-3" />
            Sending…
          </div>
        )}
        {isFailedSend && (
          <div
            className="mt-1.5 flex flex-wrap items-center gap-2"
            data-testid="message-failed-indicator"
          >
            <span className="text-owner-bubble-foreground/90 flex items-center gap-1 text-[11px] font-medium">
              <AlertCircle className="size-3" />
              Not sent — check your connection
            </span>
            {!readOnly && onRetryMessage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRetryMessage(message.id)}
                className="border-owner-bubble-foreground/40 text-owner-bubble-foreground hover:bg-owner-bubble-foreground/10 h-6 gap-1 bg-transparent text-[11px]"
                data-testid="retry-user-message-button"
              >
                <RotateCcw className="size-3" />
                Retry
              </Button>
            )}
          </div>
        )}

        {/* Retry button on error messages */}
        {!readOnly && message.isError && onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="mt-2 gap-1.5 text-xs"
            data-testid="retry-message-button"
          >
            <RotateCcw className="size-3" />
            Retry
          </Button>
        )}

        {/* Metadata cards — only on assistant messages */}
        {!isUser && message.metadata && (
          <ChatMessageMetadataCards
            message={message}
            readOnly={readOnly}
            onEditImage={onEditImage}
            onDraftEdit={onDraftEdit}
            onDraftSchedule={onDraftSchedule}
            onDraftDelete={onDraftDelete}
            onScheduleResolve={onScheduleResolve}
            onScheduleDismiss={onScheduleDismiss}
          />
        )}
      </div>

      {/* Full-size attachment dialog */}
      <Dialog open={!!expandedAttachment} onOpenChange={() => setExpandedAttachment(null)}>
        <DialogContent className="max-h-[90vh] border-none bg-transparent p-0 shadow-none sm:max-w-[90vw]">
          <DialogTitle className="sr-only">Attachment preview</DialogTitle>
          <button
            type="button"
            onClick={() => setExpandedAttachment(null)}
            className="bg-foreground/60 text-background hover:bg-foreground/80 absolute top-2 right-2 z-50 flex size-8 items-center justify-center rounded-full"
            aria-label="Close attachment preview"
          >
            <X className="size-4" />
          </button>
          {expandedAttachment &&
            (expandedAttachment.content_type?.startsWith('video/') ? (
              // Native playback of a file the user just uploaded — the browser
              // decodes it, we still never touch the frames ourselves.
              <video
                src={expandedAttachment.url}
                controls
                playsInline
                className="max-h-[85vh] max-w-full rounded-lg"
                data-testid="expanded-video-attachment"
              />
            ) : (
              <img
                src={expandedAttachment.url}
                alt="Expanded attachment"
                className="max-h-[85vh] max-w-full rounded-lg object-contain"
              />
            ))}
        </DialogContent>
      </Dialog>
    </div>
  )
}
